import React, { ReactElement } from 'react';
import { MoreOutlined } from '@ant-design/icons';
import { MenuProps } from 'antd';
import { Trans } from 'react-i18next';
import { Button } from 'common/components/antd/Button/Button';
import { Dropdown } from 'common/components/antd/Dropdown/Dropdown';
import { ColumnsType } from 'common/components/antd/Table/Table';
import { Tag } from 'common/components/antd/Tag/Tag';
import { EQueryParams } from 'domains/accounts/constants/accounts.constants';
import { makeAccountFilterByGUIDOrIBAN } from 'domains/accounts/utils/acc-iban-guid';
import { RenderAccountIBAN, RenderGUID } from 'domains/accounts/utils/render-account-iban';
import { SStatusCell } from '../customers.page.styles';
import {
  EAccountManagementCustomersColumnKeys,
  EAccountManagementCustomersInnerColumnKeys,
} from '../enums/customers.enum';
import {
  TCustomersAccountRecord,
  TItemClickEventHandler,
  TCustomerAccountsInnerRecord,
} from '../types/customers.types';

export const getColumns = (): ColumnsType<TCustomersAccountRecord> => [
  {
    title: <Trans i18nKey="accountManagement.customers.table.column.customerExternalId" />,
    width: 399,
    key: EAccountManagementCustomersColumnKeys.ExternalID,
    dataIndex: EAccountManagementCustomersColumnKeys.ExternalID,
    searchable: true,
    render: (value) => <b>{value}</b>,
  },
  {
    title: <Trans i18nKey="accountManagement.customers.table.column.branch" />,
    width: 325,
    key: EAccountManagementCustomersColumnKeys.Branch,
    dataIndex: EAccountManagementCustomersColumnKeys.Branch,
  },
  {
    title: <Trans i18nKey="accountManagement.customers.table.column.accounts" />,
    width: 325,
    key: EAccountManagementCustomersColumnKeys.Accounts,
    dataIndex: EAccountManagementCustomersColumnKeys.Accounts,
  },
  {
    title: <Trans i18nKey="accountManagement.customers.table.column.currencies" />,
    width: 325,
    key: EAccountManagementCustomersColumnKeys.Currencies,
    dataIndex: EAccountManagementCustomersColumnKeys.Currencies,
    render: (value) => (
      <div style={{ display: 'flex', gap: '5px' }}>
        {value.map((item: string) => (
          <Tag key={item}>{item}</Tag>
        ))}
      </div>
    ),
  },
];

export const getInnerColumns = ({
  menuItems,
  onItemActionClick,
}: {
  menuItems?: MenuProps['items'];
  onItemActionClick?: TItemClickEventHandler;
} = {}): ColumnsType<TCustomerAccountsInnerRecord> => [
  {
    title: '#',
    width: 60,
    key: EAccountManagementCustomersInnerColumnKeys.RowNumber,
    dataIndex: EAccountManagementCustomersInnerColumnKeys.RowNumber,
  },
  {
    title: <Trans i18nKey="accountManagement.customers.table.column.accountGuid" />,
    width: 234,
    key: EAccountManagementCustomersInnerColumnKeys.AccountGuid,
    dataIndex: EAccountManagementCustomersInnerColumnKeys.AccountGuid,
    render: (value, record, index): ReactElement =>
      <RenderGUID value={value} index={index} />,
  },
  {
    title: <Trans i18nKey="accountManagement.customers.table.column.accountNumber" />,
    width: 399,
    key: EAccountManagementCustomersInnerColumnKeys.AccountNumber,
    dataIndex: EAccountManagementCustomersInnerColumnKeys.AccountNumber,
    searchable: true,
    onFilter: (value, record): boolean => {
      const normalizedValue = String(value).replaceAll(' ', '');
      const { filter, error } = makeAccountFilterByGUIDOrIBAN(normalizedValue);
      if (filter.field === EQueryParams.IBAN) return record.iban === normalizedValue;
      if (filter.field === EQueryParams.WalletAccountGUID) return record.account_number === normalizedValue;
      if (error) return true;
      return true;
    },
    render: (value, record, index): ReactElement =>
      <RenderAccountIBAN record={record} index={index} />,
  },
  // ...(IS_ADMIN_FE
  //   ? [
  //       {
  //         title: <Trans i18nKey="accountManagement.customers.table.column.stellarAddress" />,
  //         width: 325,
  //         key: EAccountManagementCustomersInnerColumnKeys.StellarAddress,
  //         dataIndex: EAccountManagementCustomersInnerColumnKeys.StellarAddress,
  //         render: renderStellarAddress,
  //       },
  //     ]
  //   : []),
  {
    title: <Trans i18nKey="accountManagement.customers.table.column.currency" />,
    width: 325,
    key: EAccountManagementCustomersInnerColumnKeys.Currency,
    dataIndex: EAccountManagementCustomersInnerColumnKeys.Currency,
    render: (value) => <Tag>{value}</Tag>,
  },
  {
    title: <Trans i18nKey="accountManagement.customers.table.column.actualBalance" />,
    width: 170,
    key: EAccountManagementCustomersInnerColumnKeys.ActualBalance,
    dataIndex: EAccountManagementCustomersInnerColumnKeys.ActualBalance,
    render: (value, record: TCustomerAccountsInnerRecord) => record.assets[0].balance,
  },
  {
    title: <Trans i18nKey="accountManagement.customers.table.column.accountName" />,
    width: 325,
    key: EAccountManagementCustomersInnerColumnKeys.AccountName,
    dataIndex: EAccountManagementCustomersInnerColumnKeys.AccountName,
  },
  {
    title: <Trans i18nKey="accountManagement.customers.table.column.accountStatus" />,
    width: 325,
    key: EAccountManagementCustomersInnerColumnKeys.AccountStatus,
    dataIndex: EAccountManagementCustomersInnerColumnKeys.AccountStatus,
    render: (status: number) => (
      <SStatusCell>
        <Tag color={[undefined, 'green', 'volcano'][status]}>
          {
            [
              <Trans i18nKey="accountManagement.customers.table.column.accountStatus.status.closed" />,
              <Trans i18nKey="accountManagement.customers.table.column.accountStatus.status.active" />,
              <Trans i18nKey="accountManagement.customers.table.column.accountStatus.status.blocked" />,
            ][status]
          }
        </Tag>
      </SStatusCell>
    ),
  },
  {
    title: '',
    width: 20,
    key: EAccountManagementCustomersInnerColumnKeys.Menu,
    dataIndex: EAccountManagementCustomersInnerColumnKeys.Menu,
    searchable: false,
    render: (status: number, record) => (
      <SStatusCell>
        <Dropdown
          menu={{
            items: menuItems,
            onClick: onItemActionClick?.({ item: record }),
          }}
          placement="topRight"
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      </SStatusCell>
    ),
  },
];
